import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductService, Product } from '../../../../../services/product.service';
import { WebsiteService } from '../../../../../services/website.service';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

@Component({
  selector: 'app-product-selector-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './product-selector-modal.component.html',
  styleUrls: ['./product-selector-modal.component.scss']
})
export class ProductSelectorModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() show = false;
  @Input() selectedProductIds: string[] = [];
  @Input() currentLocale: string = 'nl';
  @Output() onSelect = new EventEmitter<string[]>();
  @Output() onClose = new EventEmitter<void>();

  products: Product[] = [];
  filteredProducts: Product[] = [];
  loading = false;
  searchQuery: string = '';
  selectedProducts: Set<string> = new Set();

  private destroy$ = new Subject<void>();

  constructor(
    private productService: ProductService,
    private websiteService: WebsiteService
  ) {}

  ngOnInit() {
    if (this.show) {
      this.loadProducts();
      this.initializeSelection();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show'] && changes['show'].currentValue && !changes['show'].previousValue) {
      this.loadProducts();
      this.initializeSelection();
    }
    if (changes['selectedProductIds']) {
      this.initializeSelection();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeSelection() {
    this.selectedProducts = new Set(this.selectedProductIds || []);
  }

  loadProducts() {
    this.loading = true;
    this.productService.getProducts()
      .pipe(
        takeUntil(this.destroy$),
        map(products => {
          // Filter products by current domain
          const currentDomain = this.websiteService.getCurrentDomain();
          if (currentDomain) {
            return products.filter(p => {
              const domains = p['domains'] || [];
              return domains.includes(currentDomain);
            });
          }
          return products;
        })
      )
      .subscribe({
        next: (products) => {
          this.products = products;
          this.filterProducts();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading products:', error);
          this.loading = false;
        }
      });
  }

  filterProducts() {
    if (!this.searchQuery.trim()) {
      this.filteredProducts = this.products;
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredProducts = this.products.filter(p => {
      const acf = p.acf || {};
      const name = this.getProductName(p).toLowerCase();
      const sku = (p.sku || '').toLowerCase();
      const description = this.getProductDescription(p).toLowerCase();
      
      return name.includes(query) || 
             sku.includes(query) || 
             description.includes(query);
    });
  }

  onSearchChange() {
    this.filterProducts();
  }

  toggleProduct(productId: string) {
    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);
    } else {
      this.selectedProducts.add(productId);
    }
  }

  isSelected(productId: string): boolean {
    return this.selectedProducts.has(productId);
  }

  confirmSelection() {
    const selectedIds = Array.from(this.selectedProducts);
    this.onSelect.emit(selectedIds);
    this.onClose.emit();
  }

  close() {
    this.onClose.emit();
  }

  getProductName(product: Product): string {
    const acf = product.acf || {};
    const locale = this.currentLocale;
    return acf[`naam_${locale}`] || acf['naam_nl'] || product.sku || 'Unnamed Product';
  }

  getProductDescription(product: Product): string {
    const acf = product.acf || {};
    const locale = this.currentLocale;
    const desc = acf[`omschrijving_${locale}`] || acf['omschrijving_nl'] || '';
    // Strip HTML tags for preview
    return desc.replace(/<[^>]*>/g, '').substring(0, 100) + (desc.length > 100 ? '...' : '');
  }

  getProductImage(product: Product): string {
    const images = product['images'];
    if (images && Array.isArray(images) && images.length > 0) {
      return images[0].src || '';
    }
    return '';
  }

  getProductPrice(product: Product): number {
    return product.price || product['regular_price'] || 0;
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  }

  getSelectedCount(): number {
    return this.selectedProducts.size;
  }
}
